import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Table, Button, Space, Tag, Modal, Form, Input, Select, message, Popconfirm, Descriptions, List } from 'antd';

const { Title, Paragraph } = Typography;

const statusTag = (s) => <Tag color={s === 'active' ? 'green' : s === 'pending' ? 'orange' : 'red'}>{s}</Tag>;

const WhiteLabelAgenciesList = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logs, setLogs] = useState([]);

  const authHeaders = (base = {}) => {
    const token = sessionStorage.getItem('token');
    return token ? { ...base, Authorization: `Bearer ${token}` } : base;
  };

  useEffect(() => {
    let mounted = true;
    async function load() {
      try {
        setLoading(true);
        const res = await fetch(`/api/whitelabel-agencies?limit=5000&offset=0`);
        if (!res.ok) throw new Error(`Failed to load: ${res.status}`);
        const json = await res.json();
        if (mounted) setData(Array.isArray(json.items) ? json.items : []);
      } catch (e) {
        message.error(e.message || 'Failed to load');
        if (mounted) setData([]);
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  const openDetails = async (record) => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    setDetailsData(null);
    try {
      const res = await fetch(`/api/whitelabel-agencies/${record.id}`);
      if (!res.ok) throw new Error(`Failed to load details: ${res.status}`);
      const json = await res.json();
      setDetailsData(json);
    } catch (e) {
      message.error(e.message || 'Failed to load details');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const res = await fetch(`/api/whitelabel-agencies/${record.id}`, {
        method: 'DELETE',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
      });
      if (!res.ok && res.status !== 204) throw new Error(`Delete failed: ${res.status}`);
      setData((prev) => prev.filter((row) => row.id !== record.id));
      message.success('Deleted');
    } catch (e) {
      message.error(e.message || 'Delete failed');
    }
  };

  const handleToggleStatus = async (record, toStatus) => {
    try {
      const res = await fetch(`/api/whitelabel-agencies/${record.id}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ status: toStatus }),
      });
      if (!res.ok) throw new Error(`Status update failed: ${res.status}`);
      const updated = await res.json();
      setData((prev) => prev.map((row) => (row.id === record.id ? updated : row)));
      message.success(`Status set to ${toStatus}`);
    } catch (e) {
      message.error(e.message || 'Failed to update status');
    }
  };

  const handleEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      name: record.name,
      domain: record.domain,
      logo_url: record.logo_url,
      owner_user_id: record.owner_user_id,
      status: record.status,
    });
    setEditOpen(true);
  };

  const handleCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ status: 'active' });
    setEditOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editing && editing.id) {
        const res = await fetch(`/api/whitelabel-agencies/${editing.id}`, {
          method: 'PUT',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(values),
        });
        if (!res.ok) throw new Error(`Update failed: ${res.status}`);
        const updated = await res.json();
        setData((prev) => prev.map((row) => (row.id === editing.id ? updated : row)));
        message.success('Updated');
      } else {
        const res = await fetch(`/api/whitelabel-agencies`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(values),
        });
        if (!res.ok) throw new Error(`Create failed: ${res.status}`);
        const created = await res.json();
        setData((prev) => [created, ...prev]);
        message.success('Created');
      }
      setEditOpen(false);
      setEditing(null);
    } catch (e) {
      if (e?.errorFields) return; // antd validation
      message.error(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 100 },
    { title: 'Agency Name', dataIndex: 'name', key: 'name' },
    { title: 'Primary Domain', dataIndex: 'domain', key: 'domain' },
    { title: 'Owner User ID', dataIndex: 'owner_user_id', key: 'owner_user_id' },
    { title: 'Status', dataIndex: 'status', key: 'status', render: statusTag },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at' },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button size="small" onClick={() => openDetails(record)}>Details</Button>
          <Button size="small" onClick={async () => {
            setLogsOpen(true);
            setLogsLoading(true);
            setLogs([]);
            try {
              const qs = new URLSearchParams({ type: 'agency', message_ilike: String(record.id) });
              const res = await fetch(`/api/logs?${qs.toString()}`);
              if (!res.ok) throw new Error(`Failed to load logs: ${res.status}`);
              const json = await res.json();
              setLogs(Array.isArray(json.items) ? json.items : []);
            } catch (e) {
              message.error(e.message || 'Failed to load logs');
            } finally {
              setLogsLoading(false);
            }
          }}>Activity Logs</Button>
          <Button size="small" type="primary" onClick={() => handleEdit(record)}>Edit</Button>
          {record.status === 'active' ? (
            <Popconfirm title="Deactivate agency?" onConfirm={() => handleToggleStatus(record, 'disabled')} okText="Deactivate" okButtonProps={{ danger: true }}>
              <Button size="small" danger>Deactivate</Button>
            </Popconfirm>
          ) : (
            <Popconfirm title="Activate agency?" onConfirm={() => handleToggleStatus(record, 'active')} okText="Activate">
              <Button size="small" type="default">Activate</Button>
            </Popconfirm>
          )}
          <Popconfirm title="Delete agency?" onConfirm={() => handleDelete(record)} okText="Delete" okButtonProps={{ danger: true }}>
            <Button size="small" danger>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb
        items={[
          { title: 'Admin' },
          { title: 'Agencies' },
          { title: 'White-Label Agencies' },
        ]}
      />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>White-Label Agencies</Title>
        <Space>
          <Button type="primary" onClick={handleCreate}>New White-Label Agency</Button>
        </Space>
      </div>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Loaded from <code>/api/whitelabel-agencies</code>. Status supports active/pending/disabled.
        </Paragraph>
        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        open={editOpen}
        title={editing ? `Edit ${editing.name}` : 'New White-Label Agency'}
        onOk={handleSave}
        onCancel={() => setEditOpen(false)}
        okText="Save"
        okButtonProps={{ loading: saving }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Agency Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="domain" label="Primary Domain">
            <Input />
          </Form.Item>
          <Form.Item name="logo_url" label="Logo URL">
            <Input />
          </Form.Item>
          <Form.Item name="owner_user_id" label="Owner User ID">
            <Input />
          </Form.Item>
          <Form.Item name="status" label="Status" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'active', label: 'Active' },
                { value: 'pending', label: 'Pending' },
                { value: 'disabled', label: 'Disabled' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={detailsOpen}
        title="White-Label Agency Details"
        footer={null}
        onCancel={() => setDetailsOpen(false)}
      >
        {detailsLoading ? (
          <Paragraph>Loading...</Paragraph>
        ) : detailsData ? (
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="ID">{detailsData.id}</Descriptions.Item>
            <Descriptions.Item label="Name">{detailsData.name}</Descriptions.Item>
            <Descriptions.Item label="Domain">{detailsData.domain || ''}</Descriptions.Item>
            <Descriptions.Item label="Logo URL">{detailsData.logo_url || ''}</Descriptions.Item>
            <Descriptions.Item label="Owner User ID">{detailsData.owner_user_id ?? ''}</Descriptions.Item>
            <Descriptions.Item label="Status">{detailsData.status}</Descriptions.Item>
            <Descriptions.Item label="Created At">{detailsData.created_at ? new Date(detailsData.created_at).toLocaleString() : ''}</Descriptions.Item>
          </Descriptions>
        ) : (
          <Paragraph type="secondary">No details.</Paragraph>
        )}
      </Modal>

      <Modal
        open={logsOpen}
        title="White-Label Agency Activity Logs"
        footer={null}
        onCancel={() => setLogsOpen(false)}
        width={720}
      >
        {logsLoading ? (
          <Paragraph>Loading...</Paragraph>
        ) : (
          <List
            dataSource={logs}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  title={`${new Date(item.timestamp).toLocaleString()} — ${item.type}`}
                  description={item.message}
                />
                <div>User #{item.user_id ?? ''}</div>
              </List.Item>
            )}
          />
        )}
      </Modal>
    </div>
  );
};

export default WhiteLabelAgenciesList;
